import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import api from '../utils/api';
import { useSocket, getSocket } from '../hooks/useSocket';
import AssignTrainerModal from './AssignTrainerModal';
import { assessmentBasisLabel, assessmentBasisHint } from '../utils/assessmentBasis';

const STATUS_MAP = {
  scheduled: { label: 'Scheduled', cls: 'badge-scheduled' },
  ongoing:   { label: 'Ongoing',   cls: 'badge-ongoing'   },
  ended:     { label: 'Ended',     cls: 'badge-ended'     },
  cancelled: { label: 'Cancelled', cls: 'badge-cancelled' },
};

// ── SGT time helpers ─────────────────────────────────────────────────
// We do NOT rely on the browser/system clock for SGT.
// On mount, we fetch the server time from /api/server-time (Node.js runs in SGT),
// compute the drift offset between server SGT and browser local time,
// then apply that offset to every subsequent tick.
// This means even if the user's OS clock or timezone is wrong, the QR window
// logic always reflects actual Singapore time.

const SGT = 'Asia/Singapore';

// Parse a stored session date+time as an SGT moment.
// sessionDate may arrive as a JS Date object, ISO string, or plain YYYY-MM-DD.
// startTime may arrive as HH:mm:ss or HH:mm.
// We extract only the date digits and time digits to build a clean format.
function parseSGT(sessionDate, startTime) {
  // Safely extract YYYY-MM-DD regardless of input type
  let dateOnly;
  if (sessionDate instanceof Date) {
    // JS Date — use moment to format it (no string parsing risk)
    dateOnly = moment(sessionDate).format('YYYY-MM-DD');
  } else {
    // String: "2026-06-16", "2026-06-16T00:00:00.000Z", etc.
    dateOnly = String(sessionDate).slice(0, 10);
  }

  // Safely extract HH:mm:ss — take only first 8 chars to strip any sub-seconds
  const timeOnly = String(startTime).slice(0, 8).padEnd(8, ':00');

  return moment.tz(`${dateOnly} ${timeOnly}`, 'YYYY-MM-DD HH:mm:ss', SGT);
}

// Returns current time corrected to SGT using a server-provided offset (ms)
function nowWithOffset(offsetMs) {
  return moment(Date.now() + offsetMs).tz(SGT);
}

function getMinutesUntilStart(sessionDate, startTime, now) {
  return parseSGT(sessionDate, startTime).diff(now, 'minutes');
}

function formatCountdown(sessionDate, startTime, now) {
  const start    = parseSGT(sessionDate, startTime);
  const diffMins = start.diff(now, 'minutes');
  const diffDays = start.clone().startOf('day').diff(now.clone().startOf('day'), 'days');

  if (diffMins <= 0) return null;

  if (diffDays > 1) return { text: `${diffDays} days away`, color: 'var(--tmis-chip-ended-text)', bg: 'var(--tmis-chip-ended-bg)', icon: 'bi-calendar3' };
  if (diffDays === 1) return { text: 'Tomorrow', color: 'var(--tmis-chip-ended-text)', bg: 'var(--tmis-chip-ended-bg)', icon: 'bi-calendar3' };

  const h = Math.floor(diffMins / 60);
  const m = diffMins % 60;
  if (h > 0) return { text: `Starts in ${h}h ${m}m`, color: 'var(--tmis-chip-late-text)', bg: 'var(--tmis-chip-late-bg)', icon: 'bi-clock' };
  return { text: `Starts in ${m} min`, color: 'var(--tmis-chip-late-text)', bg: 'var(--tmis-chip-late-bg)', icon: 'bi-clock' };
}

// ── Toggle switch ────────────────────────────────────────────────────
function QrToggle({ label, visible, onShow, onHide }) {
  return (
    <div className="d-flex align-items-center justify-content-between mb-2">
      <span className="fw-semibold" style={{ fontSize: '0.85rem' }}>{label}</span>
      <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.75rem', color: 'var(--tmis-muted)' }}>
        {/* <span>{visible ? 'Visible' : 'Hidden'}</span> */}
        <div
          onClick={visible ? onHide : onShow}
          role="switch"
          aria-checked={visible}
          tabIndex={0}
          onKeyDown={e => e.key === 'Enter' && (visible ? onHide() : onShow())}
          title={visible ? 'Click to hide QR' : 'Click to show QR'}
          style={{
            width: 40, height: 22, borderRadius: 11,
            background: visible ? 'var(--tmis-teal-btn)' : 'var(--tmis-border-strong)',
            cursor: 'pointer', position: 'relative',
            transition: 'background 0.2s', flexShrink: 0,
          }}
        >
          <div style={{
            width: 16, height: 16, borderRadius: '50%',
            background: 'white', position: 'absolute',
            top: 3, left: visible ? 21 : 3,
            transition: 'left 0.2s',
            boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
          }} />
        </div>
      </div>
    </div>
  );
}

// ── ActionButtons ────────────────────────────────────────────────────
// Desktop (≥992px): all buttons visible
// Tablet (768–991px): secondary actions collapse into ⋯ dropdown
// Mobile (<768px): same as tablet
function ActionButtons({
  session, showQr, setShowQr, withinWindow, sessionEnd,
  containsKeyword, canEdit, canDelete, canCreate, canEnd,
  onEdit, onEnd, onDelete, onDuplicate, onAssignTrainer,
  onLoginRecorded,
  deleting, setDeleting, ending, setEnding,
}) {

  async function handleDelete() {
    setDeleting(true);
    try {
      await onDelete();
    } finally {
      setDeleting(false);
    }
  }

  async function handleEnd() {
    setEnding(true);
    try {
      await onEnd();
    } finally {
      setEnding(false);
    }
  }

  async function handleShowQr() {
    const opening = !showQr;
    if (opening && withinWindow && session.status !== 'ended') {
      // Complete the first-click login record before the QR fetch starts so
      // scans immediately after display use the trainer's actual QR time.
      const response = await api.post(`/api/sessions/${session.id}/log-login`).catch(() => null);
      if (response?.data?.trainer_login_start) onLoginRecorded(response.data.trainer_login_start);
    }
    setShowQr(opening);
  }

  const isCancelled = session.status === 'cancelled';
  const isEnded = session.status === 'ended';
  const isOngoing = session.status === 'ongoing';
  const [menuOpen, setMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 992);
 
  React.useEffect(() => {
    function onResize() { setIsDesktop(window.innerWidth >= 992); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
 
  React.useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e) {
      if (!e.target.closest('.action-overflow-menu')) setMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);
 
  const hasSecondary = (canEdit && sessionEnd && !isCancelled) ||
    (containsKeyword && canEdit && sessionEnd && !isCancelled) ||
    (canCreate && sessionEnd) ||
    (canDelete && !isCancelled && !isEnded);
 
  // ── Overflow dropdown (tablet + mobile) ──
  const OverflowMenu = () => (
    <div className="action-overflow-menu" style={{ position: 'relative' }}>
      <button className="btn btn-sm btn-outline-secondary" style={{ padding: '5px 10px' }}
        title="More actions" onClick={() => setMenuOpen(o => !o)}>
        <i className="bi bi-three-dots" />
      </button>
      {menuOpen && (
        <div style={{
          position: 'absolute', right: 0, top: '110%', zIndex: 1050,
          background: 'var(--tmis-card-bg)', border: '1px solid var(--tmis-border)', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: 170, padding: '4px 0',
        }}>
          {canEdit && sessionEnd && !isCancelled && (
            <button className="btn btn-sm w-100 text-start d-flex align-items-center gap-2 px-3 py-2"
              style={{ border: 'none', background: 'transparent', color: 'var(--tmis-heading)' }}
              onClick={() => { onEdit(); setMenuOpen(false); }}>
              <i className="bi bi-pencil" style={{ width: 16 }} />Edit Session
            </button>
          )}
          {containsKeyword && canEdit && sessionEnd && !isCancelled && (
            <button className="btn btn-sm w-100 text-start d-flex align-items-center gap-2 px-3 py-2"
              style={{ border: 'none', background: 'transparent', color: 'var(--tmis-heading)' }}
              onClick={() => { onAssignTrainer(); setMenuOpen(false); }}>
              <i className="bi bi-person-badge" style={{ width: 16 }} />Assign Trainer
            </button>
          )}
          {canCreate && sessionEnd && (
            <button className="btn btn-sm w-100 text-start d-flex align-items-center gap-2 px-3 py-2"
              style={{ border: 'none', background: 'transparent', color: 'var(--tmis-heading)' }}
              onClick={() => { onDuplicate(); setMenuOpen(false); }}>
              <i className="bi bi-copy" style={{ width: 16 }} />Duplicate
            </button>
          )}
          {hasSecondary && canDelete && !isCancelled && !isEnded && (
            <div style={{ height: 1, background: 'var(--tmis-border)', margin: '4px 12px' }} />
          )}
          {canDelete && !isCancelled && !isEnded && (
            <button className="btn btn-sm w-100 text-start d-flex align-items-center gap-2 px-3 py-2"
              style={{ border: 'none', background: 'transparent', color: 'var(--tmis-absent)' }}
              disabled={deleting}
              onClick={() => { setMenuOpen(false); handleDelete(); }}>
              {deleting
                ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} />
                : <i className="bi bi-trash" style={{ width: 16 }} />}
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
        </div>
      )}
    </div>
  );
 
  return (
    <div className="pt-sm d-flex align-items-center gap-1 justify-content-end flex-wrap">
      {isDesktop ? (
        /* ── Desktop: all buttons visible ── */
        <>
          {canEdit && sessionEnd && !isCancelled && (
            <button className="btn btn-sm btn-outline-secondary" onClick={onEdit} title="Edit" style={{ padding: '5px 10px' }}>
              <i className="bi bi-pencil" />
            </button>
          )}
          {containsKeyword && canEdit && sessionEnd && !isCancelled && (
            <button className="btn btn-sm btn-outline-secondary" onClick={onAssignTrainer} title="Assign Trainer" style={{ padding: '5px 10px' }}>
              <i className="bi bi-person-badge" />
            </button>
          )}
          {canCreate && sessionEnd && (
            <button className="btn btn-sm btn-outline-secondary" onClick={onDuplicate} title="Duplicate" style={{ padding: '5px 10px' }}>
              <i className="bi bi-copy" />
            </button>
          )}
          {canEnd && isOngoing && !isCancelled && (
            <button className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1" onClick={handleEnd} title="End Class" style={{ padding: '5px 12px' }} disabled={ending}>
              {ending
                ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} />
                : <i className="bi bi-stop-circle-fill" />}
              <span>{ending ? 'Ending…' : 'End'}</span>
            </button>
          )}
          {canDelete && !isCancelled && !isEnded && (
            <button className="btn btn-sm btn-outline-danger d-flex align-items-center"
              onClick={handleDelete} title="Delete" style={{ padding: '5px 10px' }}
              disabled={deleting}>
              {deleting
                ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} />
                : <i className="bi bi-trash" />}
            </button>
          )}
        </>
      ) : (
        /* ── Tablet / Mobile: overflow dropdown + End ── */
        <>
          {hasSecondary && <OverflowMenu />}
          {canEnd && isOngoing && !isCancelled && (
            <button className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1" onClick={handleEnd} title="End Class" style={{ padding: '5px 12px' }} disabled={ending}>
              {ending
                ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} />
                : <i className="bi bi-stop-circle-fill" />}
              <span>{ending ? 'Ending…' : 'End'}</span>
            </button>
          )}
        </>
      )}

      {/* ── Show QR — hidden once the session is cancelled ── */}
      {!isCancelled && (
        <button
          className={`btn btn-sm d-flex align-items-center gap-1 ${showQr ? 'btn-tmis-primary' : 'btn-tmis-outline'}`}
          style={{ padding: '5px 14px' }}
          onClick={handleShowQr}
        >
          <i className={`bi ${showQr ? 'bi-qr-code-scan' : 'bi-qr-code'}`} />
          <span>{showQr ? 'Hide QR' : 'Show QR'}</span>
        </button>
      )}
    </div>
  );
}
 

export default function SessionCard({ session, onEdit, onDelete, onDuplicate, onEnd, canEdit, canDelete, canCreate, user }) {
  const [qrData, setQrData]           = useState(null);
  const [qrLoading, setQrLoading]     = useState(false);
  const [showQr, setShowQr]           = useState(false);
  const [enlargedQr, setEnlargedQr]   = useState(null);
  const [attendance, setAttendance]   = useState(null);
  const attendanceRequestRef = useRef(false);
  const [showRecords, setShowRecords] = useState(false); // show list by default
  const [showAssignTrainer, setShowAssignTrainer] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [ending, setEnding] = useState(false);
  const [checkinVisible, setCheckinVisible]   = useState(true);
  const [checkoutVisible, setCheckoutVisible] = useState(false);
  const [trainerLoginTime, setTrainerLoginTime] = useState(session.trainer_login_start || null);
  // sgtOffset: difference (ms) between server SGT and browser Date.now()
  // Fetched once on mount from /api/server-time so we never trust the system clock.
  const [sgtOffset, setSgtOffset] = useState(0);
  const [now, setNow] = useState(() => moment().tz(SGT));
  // qrWindowMinutes: admin-configurable via Settings (system_settings.qr_window_minutes).
  // Defaults to 30 until the fetch resolves.
  const [qrWindowMinutes, setQrWindowMinutes] = useState(30);

  useEffect(() => {
    setTrainerLoginTime(session.trainer_login_start || null);
  }, [session.trainer_login_start]);

  useEffect(() => {
    api.get('/api/settings')
      .then(res => {
        const mins = parseInt(res.data?.data?.qr_window_minutes);
        if (!isNaN(mins) && mins > 0) setQrWindowMinutes(mins);
      })
      .catch(() => {});
  }, []);

  // ── Fetch server SGT time once on mount ──────────────────────────
  useEffect(() => {
    const API = process.env.REACT_APP_API_URL || '';
    const clientBefore = Date.now();
    fetch(`${API}/api/server-time`)
      .then(r => r.json())
      .then(data => {
        const clientAfter = Date.now();
        const roundTrip = clientAfter - clientBefore;
        // Server timestamp is in SGT; compute how far browser clock deviates
        const serverMs = new Date(data.sgt_iso).getTime();
        const estimatedServerNow = serverMs + roundTrip / 2;
        const offset = estimatedServerNow - clientAfter;
        setSgtOffset(offset);
        setNow(nowWithOffset(offset));
      })
      .catch(() => {
        // Fallback: use browser time converted to SGT (best effort)
        setNow(moment().tz(SGT));
      });
  }, []);

  // ── Tick every second using the corrected offset ─────────────────
  useEffect(() => {
    const timer = setInterval(() => setNow(nowWithOffset(sgtOffset)), 1000);
    return () => clearInterval(timer);
  }, [sgtOffset]);

  // ── Compute window state from live clock ─────────────────────────
  const minsUntilStart = getMinutesUntilStart(session.session_date, session.start_time, now);
  const withinWindow   = minsUntilStart <= qrWindowMinutes && minsUntilStart > -Infinity;
  // moment's diff(..., 'minutes') truncates toward zero, so it hits 0 up to 59s
  // before the actual start — use the exact instant for the started/ongoing check.
  const sessionStarted = now.isSameOrAfter(parseSGT(session.session_date, session.start_time));
  const countdown      = formatCountdown(session.session_date, session.start_time, now);

  // Reflect "ongoing" the instant the clock reaches start_time, without
  // waiting for the server's scheduler tick / socket round-trip. The
  // server status is still the source of truth for every other state
  // (ended/cancelled) and will confirm 'ongoing' shortly after.
  const displayStatus = (session.status === 'scheduled' && sessionStarted) ? 'ongoing' : session.status;
  const attendancePending = session.status === 'scheduled' && !sessionStarted;
  const attendanceIsActive = session.status === 'scheduled' || session.status === 'ongoing';
  const isPendingAttendance = record => attendanceIsActive && (
    (record.status === 'pending' || !record.status) ||
    (record.status === 'absent' && !record.clock_in_time)
  );

  // When window opens (crosses 30-min mark), auto-set defaults
  useEffect(() => {
    if (withinWindow) {
      setCheckinVisible(true);   // Clock-In visible by default
      setCheckoutVisible(false); // Clock-Out hidden by default
    }
  }, [withinWindow]);

  // ── Data fetchers ────────────────────────────────────────────────
  const fetchQr = useCallback(async () => {
    setQrLoading(true);
    try {
      const res = await api.get(`/api/sessions/${session.id}/qr`);
      setQrData(res.data);
    } catch { toast.error('Failed to load QR.'); }
    finally { setQrLoading(false); }
  }, [session.id]);

  const fetchAttendance = useCallback(async () => {
    if (attendanceRequestRef.current) return;
    attendanceRequestRef.current = true;
    try {
      const res = await api.get(`/api/sessions/${session.id}/attendance`);
      const records = res.data.data?.records || [];
      setAttendance({
        pending: records.filter(r => r.status === 'pending').length,
        present: records.filter(r => r.status === 'present').length,
        late:    records.filter(r => r.status === 'late').length,
        mc:      records.filter(r => r.status === 'mc').length,
        absent:  records.filter(r => r.status === 'absent').length,
        total:   records.length,
        records,
      });
    } catch {} finally {
      attendanceRequestRef.current = false;
    }
  }, [session.id]);

  // Fetch QR only when panel is opened
  useEffect(() => {
    if (showQr) { fetchQr(); }
  }, [showQr, fetchQr]);

  // Fetch attendance always on mount — student list must show regardless of QR panel
  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  // Keep the visible roster live while the session can still change. The
  // detail endpoint reconciles the session against current student/intake/
  // module statuses, so external admin updates appear without recreating it.
  useEffect(() => {
    if (session.status !== 'scheduled' && session.status !== 'ongoing') return;
    const interval = setInterval(fetchAttendance, 2000);
    return () => clearInterval(interval);
  }, [fetchAttendance, session.status]);

  // ── Socket ───────────────────────────────────────────────────────
  useEffect(() => {
    const socket = getSocket();
    socket.emit('join_session', session.id);
    return () => socket.emit('leave_session', session.id);
  }, [session.id]);

  useSocket({
    attendance_update: (data) => {
      if (data.sessionId === session.id)
        setAttendance(prev => ({ ...prev, ...data.counts, records: data.records }));
    },
    checkout_qr_visible: (data) => { if (data.sessionId === session.id && showQr) fetchQr(); },
    checkout_qr_hidden:  (data) => { if (data.sessionId === session.id && showQr) fetchQr(); },
    checkin_qr_visible:  (data) => { if (data.sessionId === session.id && showQr) fetchQr(); },
    checkin_qr_hidden:   (data) => { if (data.sessionId === session.id && showQr) fetchQr(); },
    roster_synced:       (data) => { if (data.sessionId === session.id) fetchAttendance(); },
  });

  useEffect(() => {
    if (!showQr || session.status === 'ended') return;
    const interval = setInterval(fetchQr, 60000);
    return () => clearInterval(interval);
  }, [showQr, fetchQr, session.status]);

  // ── Derived ──────────────────────────────────────────────────────
  const { label: statusLabel, cls: statusCls } = STATUS_MAP[displayStatus] || STATUS_MAP.scheduled;
  const intakes    = Array.isArray(session.intakes) ? session.intakes : [session.intakes];
  const sessionEnd = session.status !== 'ended';
  const keywords   = ['admin', 'staff'];
  const containsKeyword = keywords.some(k => user?.role?.toLowerCase().includes(k.toLowerCase()));

  // Trainers without a general 'update' permission can still end their own assigned class
  const isAssignedTrainer = !!user && (
    (session.trainer_id && String(session.trainer_id) === String(user.id)) ||
    (session.trainer_email && session.trainer_email.toLowerCase() === (user.email || '').toLowerCase())
  );
  const canEnd = canEdit || isAssignedTrainer;

  const statusIcon  = s => s === 'pending' ? '🕒' : s === 'present' ? '🟢' : s === 'late' ? '🟡' : s === 'mc' ? '🔵' : '🔴';
  const statusClass = s => s === 'pending' ? 'status-pending' : s === 'present' ? 'status-present' : s === 'late' ? 'status-late' : s === 'mc' ? 'status-mc' : 'status-absent';

  // ── QR panel content ─────────────────────────────────────────────
  function renderQrPanel() {
    const pendingAttendanceCount = attendance?.records?.filter(record =>
      isPendingAttendance(record)
    ).length || 0;
    const confirmedAbsentCount = Math.max(0, (attendance?.absent || 0) - pendingAttendanceCount);

    // Session already ended
    if (session.status === 'ended') {
      return (
        <div className="text-center py-4 text-muted">
          <i className="bi bi-stop-circle d-block fs-2 mb-2" style={{ opacity: 0.3 }} />
          <div className="fw-semibold">Session Ended</div>
          <div style={{ fontSize: '0.8rem' }}>QR codes are no longer available.</div>
        </div>
      );
    }

    // More than qrWindowMinutes away — show countdown, no QR
    if (!withinWindow) {
      const h = Math.floor(Math.max(0, minsUntilStart - qrWindowMinutes) / 60);
      const m = Math.max(0, minsUntilStart - qrWindowMinutes) % 60;
      const daysLeft = moment(`${session.session_date}`, 'YYYY-MM-DD').startOf('day').diff(moment().startOf('day'), 'days');

      return (
        <div className="text-center py-5">
          <i className="bi bi-qr-code d-block fs-1 mb-3" style={{ opacity: 0.15 }} />
          <div className="fw-bold mb-1" style={{ fontSize: '1rem', color: 'var(--tmis-text)' }}>
            QR codes not available yet
          </div>
          {daysLeft > 0 ? (
            <div className="mb-3">
              <span className="badge rounded-pill px-3 py-2" style={{ background: 'var(--tmis-chip-scheduled-bg)', color: 'var(--tmis-chip-scheduled-text)', fontSize: '0.85rem' }}>
                <i className="bi bi-calendar3 me-1" />
                {daysLeft === 1 ? 'Tomorrow' : `${daysLeft} days away`}
              </span>
            </div>
          ) : (
            <div className="mb-3">
              <span className="badge rounded-pill px-3 py-2" style={{ background: 'var(--tmis-chip-late-bg)', color: 'var(--tmis-chip-late-text)', fontSize: '0.85rem' }}>
                <i className="bi bi-clock me-1" />
                {h > 0 ? `Opens in ${h}h ${m}m` : `Opens in ${Math.max(0, minsUntilStart - qrWindowMinutes)} min`}
              </span>
            </div>
          )}
          <div className="text-muted" style={{ fontSize: '0.8rem' }}>
            QR codes will appear <strong>{qrWindowMinutes} minutes</strong> before class starts
            <br />
            <span style={{ color: 'var(--tmis-muted)' }}>
              {moment(`${session.session_date} ${session.start_time}`, 'YYYY-MM-DD HH:mm:ss').format('ddd, DD MMM YYYY [at] HH:mm')}
            </span>
          </div>
        </div>
      );
    }

    // Within 30 mins — show QR panel
    return (
      <>
        {/* Clock-out hidden notice */}
        {!checkoutVisible && (
          <div className="alert alert-warning d-flex align-items-center gap-2 py-2 px-3 mb-3" style={{ fontSize: '0.82rem', borderRadius: 8 }}>
            <i className="bi bi-eye-slash-fill flex-shrink-0" />
            <span>
              <strong>Clock-Out QR is hidden.</strong> Toggle it visible when you're ready for students to clock out.
            </span>
          </div>
        )}

        <div className="row g-3 mb-3">

          {/* ── Clock-In QR ─────────────────────────────────── */}
          <div className="col-md-4">
            <div className="qr-container">
              <QrToggle
                label={<><i className="bi bi-box-arrow-in-right text-success me-1" />Clock-In QR</>}
                visible={checkinVisible}
                onShow={() => setCheckinVisible(true)}
                onHide={() => setCheckinVisible(false)}
              />
              {checkinVisible && qrData?.qrCheckinUrl ? (
                <>
                  <img src={qrData.qrCheckinUrl} alt="Clock-In QR Code" className="img-fluid" />
                  <button className="qr-enlarge-btn" onClick={() => setEnlargedQr({ url: qrData.qrCheckinUrl, label: 'Clock-In QR' })}>
                    <i className="bi bi-arrows-fullscreen" /> Enlarge
                  </button>
                </>
              ) : (
                <div className="text-center text-muted py-4" style={{ fontSize: '0.8rem' }}>
                  <i className="bi bi-eye-slash d-block fs-3 mb-1" style={{ opacity: 0.3 }} />
                  {checkinVisible ? 'Loading QR…' : 'Hidden — toggle to show'}
                </div>
              )}
            </div>
          </div>

          {/* ── Clock-Out QR ────────────────────────────────── */}
          <div className="col-md-4">
            <div className="qr-container">
              <QrToggle
                label={<><i className="bi bi-box-arrow-right text-primary me-1" />Clock-Out QR</>}
                visible={checkoutVisible}
                onShow={() => setCheckoutVisible(true)}
                onHide={() => setCheckoutVisible(false)}
              />
              {checkoutVisible && qrData?.qrCheckoutUrl ? (
                <>
                  <img src={qrData.qrCheckoutUrl} alt="Clock-Out QR Code" className="img-fluid" />
                  <button className="qr-enlarge-btn" onClick={() => setEnlargedQr({ url: qrData.qrCheckoutUrl, label: 'Clock-Out QR' })}>
                    <i className="bi bi-arrows-fullscreen" /> Enlarge
                  </button>
                </>
              ) : (
                <div className="text-center text-muted py-4" style={{ fontSize: '0.8rem' }}>
                  <i className="bi bi-eye-slash d-block fs-3 mb-1" style={{ opacity: 0.3 }} />
                  {checkoutVisible ? 'Loading QR…' : 'Hidden — reveal when ready'}
                </div>
              )}
            </div>
          </div>

          {/* ── Attendance Summary ───────────────────────────── */}
          <div className="col-md-4">
            <div className="h-100 p-3 rounded-3 border">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                  <i className="bi bi-people me-1 text-muted" />Attendance
                </span>
                {/* {attendance?.total > 0 && (
                  <button
                    className="btn btn-xs btn-outline-secondary"
                    style={{ fontSize: '0.7rem', padding: '2px 8px' }}
                    onClick={() => setShowRecords(!showRecords)}
                  >
                    <i className={`bi ${showRecords ? 'bi-chevron-up' : 'bi-list-ul'} me-1`} />
                    {showRecords ? 'Hide' : 'View'} List
                  </button>
                )} */}
                <button
                  className="btn btn-xs btn-outline-secondary"
                  style={{ fontSize: '0.7rem', padding: '2px 8px', color: 'var(--tmis-text)' }}
                  onClick={() => setShowRecords(!showRecords)}
                >
                  <i className={`bi ${showRecords ? 'bi-chevron-up' : 'bi-list-ul'} me-1`} />
                  {showRecords ? 'Hide' : 'View'} List
                </button>
              </div>
              {attendance ? (
                <>
                  {attendancePending && (
                    <div className="attendance-pending-note">
                      <i className="bi bi-clock" aria-hidden="true" /> Attendance will begin when the session starts.
                    </div>
                  )}
                  <div className="d-flex flex-column gap-2 mb-3">
                    {[
                      { key: 'present', label: 'Present', emoji: '🟢', cls: 'present' },
                      { key: 'late',    label: 'Late',    emoji: '🟡', cls: 'late'    },
                      { key: 'mc',      label: 'MC',      emoji: '🔵', cls: 'mc'      },
                    ].concat(
                      pendingAttendanceCount > 0
                        ? [{ key: 'pending', value: pendingAttendanceCount, label: 'Pending Attendance', emoji: '🕒', cls: 'pending' }]
                        : [],
                      confirmedAbsentCount > 0 || pendingAttendanceCount === 0
                        ? [{ key: 'absent', value: confirmedAbsentCount, label: 'Absent', emoji: '🔴', cls: 'absent' }]
                        : [],
                    ).map(({ key, value, label, emoji, cls }) => (
                      <div key={key} className="d-flex justify-content-between align-items-center">
                        <span className={`stat-pill ${cls}`}><span>{emoji}</span>{label}</span>
                        <strong>{value ?? attendance[key]}</strong>
                      </div>
                    ))}
                  </div>
                  <div className="border-top pt-2" style={{ fontSize: '0.75rem', color: 'var(--tmis-muted)' }}>
                    Total: <strong>{attendance.total}</strong>
                  </div>
                </>
              ) : (
                <div className="text-muted" style={{ fontSize: '0.8rem' }}>Loading…</div>
              )}
            </div>
          </div>
        </div>

      </>
    );
  }

  // ── Student list — rendered unconditionally, always live ────────
  function renderStudentList() {
    if (!attendance) return null;
    const pendingAttendanceCount = attendanceIsActive
      ? attendance.records.filter(isPendingAttendance).length
      : 0;
    const confirmedAbsentCount = Math.max(0, attendance.absent - pendingAttendanceCount);
    return (
      <div className="mt-2 pt-2 border-top">
        <div className="d-flex justify-content-between align-items-center">
          <span style={{ fontSize: '0.8rem', color: 'var(--tmis-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <i className="bi bi-list-check text-muted" />
            <span style={{ fontWeight: 600, color: 'var(--tmis-heading)' }}>Students</span>
            <span style={{ color: 'var(--tmis-muted)' }}>({attendance.total})</span>
            {attendance.present > 0 && (
              <span className="text-center" style={{ background: 'var(--tmis-chip-ongoing-bg)', color: 'var(--tmis-chip-ongoing-text)', borderRadius: 20, padding: '1px 8px', fontSize: '0.7rem', fontWeight: 700 }}>
                {attendance.present} Present
              </span>
            )}
            {attendance.late > 0 && (
              <span className="text-center" style={{ background: 'var(--tmis-chip-late-bg)', color: 'var(--tmis-chip-late-text)', borderRadius: 20, padding: '1px 8px', fontSize: '0.7rem', fontWeight: 700 }}>
                {attendance.late} Late
              </span>
            )}
            {attendance.mc > 0 && (
              <span className="text-center" style={{ background: 'var(--tmis-chip-scheduled-bg)', color: 'var(--tmis-chip-scheduled-text)', borderRadius: 20, padding: '1px 8px', fontSize: '0.7rem', fontWeight: 700 }}>
                {attendance.mc} MC
              </span>
            )}
            {pendingAttendanceCount > 0 && (
              <span className="stat-pill pending">
                <i className="bi bi-clock" aria-hidden="true" />
                {pendingAttendanceCount} Pending Attendance
              </span>
            )}
            {confirmedAbsentCount > 0 && (
              <span className="text-center" style={{ background: 'var(--tmis-chip-cancelled-bg)', color: 'var(--tmis-chip-cancelled-text)', borderRadius: 20, padding: '1px 8px', fontSize: '0.7rem', fontWeight: 700 }}>
                {confirmedAbsentCount} Absent
              </span>
            )}
          </span>
          <button
            className="btn btn-xs btn-outline-secondary"
            style={{ fontSize: '0.7rem', padding: '2px 8px', color: 'var(--tmis-text)' }}
            onClick={() => setShowRecords(!showRecords)}
          >
            <i className={`bi ${showRecords ? 'bi-chevron-up' : 'bi-chevron-down'} me-1`} />
            {showRecords ? 'Hide' : 'Show'}
          </button>
        </div>

        {showRecords && (
          <div className="rounded-3 border overflow-hidden mt-2">
            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
              <table className="table tmis-table table-sm mb-0 table-striped table-hover">
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th className="text-center" style={{ width: 32 }}>#</th>
                    <th className="text-center">Student ID</th>
                    <th>Name</th>
                    <th className="text-center">Intake</th>
                    <th className="text-center">Clock In</th>
                    <th className="text-center">Clock Out</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {attendance.records.length === 0 ? (
                    <tr><td colSpan="7" className="text-center text-muted py-3" style={{ fontSize: '0.8rem' }}>No students found for this session.</td></tr>
                  ) : attendance.records.map((r, i) => (
                    <tr key={r.id || r.student_email}>
                      <td className="text-muted text-center" style={{ fontSize: '0.75rem' }}>{i + 1}</td>
                      <td className="text-center" style={{ fontSize: '0.8rem' }}>{r.student_id || '—'}</td>
                      <td>
                        <div style={{ fontWeight: 500, fontSize: '0.82rem' }}>{r.student_name.toUpperCase()}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--tmis-muted)' }}>{r.student_email}</div>
                      </td>
                      <td className="text-center" style={{ fontSize: '0.8rem' }}>{r.intake || '—'}</td>
                      <td className="text-center" style={{ fontSize: '0.8rem' }}>
                        {r.clock_in_time
                          ? <span className="text-success fw-semibold">
                              <i className="bi bi-box-arrow-in-right me-1" />
                              {moment(r.clock_in_time).format('HH:mm:ss')}
                            </span>
                          : <span className="text-muted">—</span>}
                      </td>
                      <td className="text-center" style={{ fontSize: '0.8rem' }}>
                        {r.clock_out_time
                          ? <span className="text-primary fw-semibold">
                              <i className="bi bi-box-arrow-right me-1" />
                              {moment(r.clock_out_time).format('HH:mm:ss')}
                            </span>
                          : <span className="text-muted">—</span>}
                      </td>
                      <td className="text-center">
                        <span className={isPendingAttendance(r) ? 'status-pending' : statusClass(r.status)}>
                          {isPendingAttendance(r) || r.status === 'pending'
                            ? <><i className="bi bi-clock me-1" aria-hidden="true" />Pending Attendance</>
                            : `${statusIcon(r.status)} ${r.status === 'mc' ? 'MC' : r.status ? r.status.charAt(0).toUpperCase() + r.status.slice(1) : '—'}`}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Status color map ──────────────────────────────────────────────
  const STATUS_COLORS = {
    scheduled: { bg: 'var(--tmis-chip-scheduled-bg)', color: 'var(--tmis-chip-scheduled-text)', dot: 'var(--tmis-accent-scheduled)' },
    ongoing:   { bg: 'var(--tmis-chip-ongoing-bg)',   color: 'var(--tmis-chip-ongoing-text)',   dot: 'var(--tmis-accent-ongoing)' },
    ended:     { bg: 'var(--tmis-chip-ended-bg)',     color: 'var(--tmis-chip-ended-text)',     dot: 'var(--tmis-accent-ended)' },
    cancelled: { bg: 'var(--tmis-chip-cancelled-bg)', color: 'var(--tmis-chip-cancelled-text)', dot: 'var(--tmis-accent-cancelled)' },
  };
  const sc = STATUS_COLORS[displayStatus] || STATUS_COLORS.scheduled;

  return (
    <>
      <div className={`session-card status-${displayStatus || 'scheduled'}`} style={{ padding: '14px 18px 14px 22px' }}>

        {/* ── Header ──────────────────────────────────────────────── */}
        <div className="d-sm-flex align-items-center justify-content-between gap-2 flex-wrap">

          {/* Left: status + title + meta */}
          <div style={{ minWidth: 0, flex: 1 }}>
            {/* Status badge + title on same line */}
            <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                background: sc.bg, color: sc.color,
                borderRadius: 20, padding: '2px 10px',
                fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.03em',
                flexShrink: 0,
              }}>
                {statusLabel.toUpperCase()}
              </span>
              <span className="fw-bold text-truncate" style={{ fontSize: '1rem', color: 'var(--tmis-heading)' }}>
                {session.module_name || session.schedule_name}
              </span>
              {session.module_name && session.schedule_name && session.module_name !== session.schedule_name && (
                <span className="text-truncate" style={{ fontSize: '0.9rem', fontWeight: 500, lineHeight: 1.5, color: 'var(--tmis-muted)' }}>
                  ({session.schedule_name})
                </span>
              )}
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: session.session_type === 'assessment' ? 'var(--tmis-chip-late-bg)' : 'var(--tmis-chip-ongoing-bg)',
                color: session.session_type === 'assessment' ? 'var(--tmis-chip-late-text)' : 'var(--tmis-chip-ongoing-text)',
                borderRadius: 20, padding: '2px 9px', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
              }}>
                <i className={`bi ${session.session_type === 'assessment' ? 'bi-clipboard2-check-fill' : 'bi-mortarboard-fill'}`} />
                {session.session_type === 'assessment' ? 'Assessment' : 'Training'}
              </span>
              {session.session_type === 'assessment' && !!session.assessment_basis && (
                <span
                  title={assessmentBasisHint(session.assessment_basis)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: 'var(--tmis-chip-scheduled-bg)', color: 'var(--tmis-chip-scheduled-text)',
                    borderRadius: 20, padding: '2px 9px', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
                  }}
                >
                  <i className="bi bi-bar-chart-steps" />
                  {assessmentBasisLabel(session.assessment_basis)}
                </span>
              )}
              {session.session_type === 'assessment' && !!session.force_present && (
                <span
                  title="Any student who scans the QR is marked Present — late/absent rules are skipped for this session."
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: 'var(--tmis-chip-scheduled-bg)', color: 'var(--tmis-chip-scheduled-text)',
                    borderRadius: 20, padding: '2px 9px', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
                  }}
                >
                  <i className="bi bi-check-circle-fill" />
                  Auto-Present
                </span>
              )}
              {session.status !== 'ended' && countdown && (
                <span style={{
                  fontSize: '0.72rem', fontWeight: 600, color: countdown.color,
                  background: countdown.bg, borderRadius: 20,
                  padding: '2px 8px', flexShrink: 0,
                }}>
                  <i className={`bi ${countdown.icon} me-1`} />{countdown.text}
                </span>
              )}
            </div>

            {/* Meta row — date / time / venue / trainer */}
            <div className="d-flex flex-wrap gap-x-3 gap-y-0" style={{ fontSize: '0.78rem', color: 'var(--tmis-muted)', columnGap: 16, rowGap: 2 }}>
              <span style={{color: 'var(--tmis-text)'}}><i className="bi bi-calendar3 me-1" />{moment(session.session_date).format('DD/MM/YYYY')}</span>
              <span style={{color: 'var(--tmis-text)'}}><i className="bi bi-clock me-1" />{String(session.start_time).slice(0,5)}{session.end_time ? ` – ${String(session.end_time).slice(0,5)}` : ''}</span>
              {session.venue_name && <span style={{color: 'var(--tmis-text)'}}><i className="bi bi-geo-alt me-1" />{session.venue_name}</span>}
              {session.trainer_name && <span style={{ color: 'var(--tmis-teal)', fontWeight: 600 }}><i className="bi bi-person-fill me-1" />{session.trainer_name}</span>}
              {trainerLoginTime && <span style={{ color: 'var(--tmis-text)' }}><i className="bi bi-box-arrow-in-right me-1" />Login: {moment.tz(trainerLoginTime, SGT).format('HH:mm')}</span>}
            </div>

            {/* Intake badges */}
            <div className="mt-1 d-flex flex-wrap gap-1">
              {intakes.filter(Boolean).map(int => (
                <span key={int} style={{
                  background: 'var(--tmis-chip-scheduled-bg)', color: 'var(--tmis-chip-scheduled-text)',
                  borderRadius: 4, padding: '1px 7px', fontSize: '0.7rem', fontWeight: 600,
                }}>{int}</span>
              ))}
            </div>
          </div>

          {/* Right: action buttons */}
          <ActionButtons
            session={session}
            showQr={showQr}
            setShowQr={setShowQr}
            withinWindow={withinWindow}
            sessionEnd={sessionEnd}
            containsKeyword={containsKeyword}
            canEdit={canEdit}
            canDelete={canDelete}
            canCreate={canCreate}
            canEnd={canEnd}
            onEdit={onEdit}
            onEnd={onEnd}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
            onAssignTrainer={() => setShowAssignTrainer(true)}
            onLoginRecorded={setTrainerLoginTime}
            deleting={deleting}
            setDeleting={setDeleting}
            ending={ending}
            setEnding={setEnding}
          />

          {/* Right: action buttons */}
          {/* <div className="d-flex align-items-center gap-1 flex-shrink-0 flex-wrap">
            {canEdit && sessionEnd && (
              <button className="btn btn-sm btn-outline-secondary" onClick={onEdit} title="Edit"
                style={{ padding: '4px 8px' }}>
                <i className="bi bi-pencil" />
              </button>
            )}
            {containsKeyword && canEdit && sessionEnd && (
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowAssignTrainer(true)} title="Assign Trainer"
                style={{ padding: '4px 8px' }}>
                <i className="bi bi-person-badge" />
              </button>
            )}
            {canCreate && sessionEnd && (
              <button className="btn btn-sm btn-outline-secondary" onClick={onDuplicate} title="Duplicate to next day"
                style={{ padding: '4px 8px' }}>
                <i className="bi bi-copy" />
              </button>
            )}
            {canEdit && sessionEnd && (
              <button className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1" onClick={onEnd} title="End Class"
                style={{ padding: '4px 10px' }}>
                <i className="bi bi-stop-circle-fill" />
                <span>End</span>
              </button>
            )}
            {canDelete && (
              <button className="btn btn-sm btn-outline-danger" onClick={onDelete} title="Delete"
                style={{ padding: '4px 8px' }}>
                <i className="bi bi-trash" />
              </button>
            )}
            <button
              className={`btn btn-sm d-flex align-items-center gap-1 ${showQr ? 'btn-tmis-primary' : 'btn-tmis-outline'}`}
              style={{ padding: '4px 12px' }}
              onClick={() => {
                const opening = !showQr;
                setShowQr(opening);
                if (opening && withinWindow && session.status !== 'ended') {
                  api.post(`/api/sessions/${session.id}/log-login`).catch(() => {});
                }
              }}
            >
              <i className={`bi ${showQr ? 'bi-qr-code-scan' : 'bi-qr-code'}`} />
              <span>{showQr ? 'Hide QR' : 'Show QR'}</span>
            </button>
          </div> */}
        </div>

        {/* ── QR Panel ────────────────────────────────────────────── */}
        {showQr && (
          <div className="mt-3 pt-3 border-top">
            {qrLoading && !qrData ? (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm" style={{ color: '#17a2b8' }} />
              </div>
            ) : renderQrPanel()}
          </div>
        )}

        {/* ── Student List — always visible, updates in real-time via socket ── */}
        {renderStudentList()}
      </div>

      {/* ── Enlarge QR Modal ──────────────────────────────────────── */}
      {enlargedQr && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.7)' }} onClick={() => setEnlargedQr(null)}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h6 className="modal-title">{enlargedQr.label}</h6>
                <button className="btn-close btn-close-white" onClick={() => setEnlargedQr(null)} />
              </div>
              <div className="modal-body text-center p-4">
                <img src={enlargedQr.url} alt={enlargedQr.label} style={{ width: '100%', maxWidth: 350 }} />
                <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.8rem' }}>
                  Students scan this QR with their phone camera
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAssignTrainer && (
        <AssignTrainerModal
          session={session}
          onClose={() => setShowAssignTrainer(false)}
          onSaved={(result) => {
            setShowAssignTrainer(false);
            if (result?.calendarSynced === false) toast.warn(result.message);
            else toast.success(result?.message || 'Trainer assigned.');
          }}
        />
      )}
    </>
  );
}
